import type { Side } from '../process/needsOpens.ts';
import { findMatches } from './find.ts';

/**
 * Choosing what a step needs or opens (design study 31, slice 5; §5, A): "+ It needs…" or "+ It opens…" pressed on a
 * step's card draws the whole board (TreeView), the step being joined ringed, every step it cannot join faint with why
 * in words (a loop; on the master one in a later phase than a step that would need it: the page's `refusal`), a press on
 * any other picking it; a bar above the board names what is being chosen, with a field to type its name and Cancel. On
 * a phone the bar and the field alone. After a pick, or Cancel, the step's card opens again. The rules are here; the
 * drawing is TreeView's, TreeBoard's and Tile's, the bar NeedsOpens.tsx's.
 */

/** A step the typed field can find, by its name as shown, and where it is when it is not on the board (a task in a step's list). */
export interface ChoiceStep {
  key: string;
  name: string;
  where?: string;
}

export interface Choosing {
  /** The step being joined, by its key in the drawing. */
  key: string;
  /** Which of its lists the step chosen goes into. */
  side: Side;
  /** Why a step cannot be chosen, in words, or null when it can (the page's rule: the master's, a job's). */
  refusal: (key: string) => string | null;
  onPick: (key: string) => void;
  onCancel: () => void;
  /** What the typed field finds, in the walk's order; by default the steps on the board, in the order given. */
  among?: readonly ChoiceStep[];
}

/** The bar's words, its field's label: on a phone the board is rows, so the name is typed. */
export const choosingWords = (name: string, side: Side, wide: boolean): string =>
  `Choosing what ${name} ${side === 'needs' ? 'needs' : 'opens'}: ${wide ? 'press a step on the board, or type its name' : 'type its name'}`;

/** How a tile is drawn while choosing: the step being joined ringed; one it cannot join faint, with why; any other picked by a press. */
export function choosingTile(key: string, c: Pick<Choosing, 'key' | 'refusal'>): { ringed: boolean; why: string | null; picks: boolean } {
  if (key === c.key) return { ringed: true, why: null, picks: false };
  const why = c.refusal(key);
  return { ringed: false, why, picks: why === null };
}

/** A tile pressed while choosing: an allowed step is picked; a refused one is not, and its why is given back to be shown. */
export function pressWhileChoosing(key: string, c: Pick<Choosing, 'key' | 'refusal' | 'onPick'>): string | null {
  const t = choosingTile(key, c);
  if (t.picks) c.onPick(key);
  return t.why;
}

/** A step the typed field found, with why it cannot be chosen (null when it can). */
export type ChoiceRow = ChoiceStep & { why: string | null };

/**
 * The steps a name typed matches, by Find's rule (those it starts first, then the order given, which is the walk's), the
 * step being joined left out, each with why it cannot be chosen; the first `limit`, and how many more there are.
 * Nothing typed, nothing found.
 */
export function choosingMatches(among: readonly ChoiceStep[], typed: string, c: Pick<Choosing, 'key' | 'refusal'>, limit = 12): { rows: ChoiceRow[]; more: number } {
  const hits = findMatches(
    among.filter((s) => s.key !== c.key).map((s) => ({ s, title: s.name })),
    typed,
  );
  return { rows: hits.slice(0, limit).map(({ s }) => ({ ...s, why: c.refusal(s.key) })), more: Math.max(0, hits.length - limit) };
}
